/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
/**
 * AbstractCanvasConnection
 * This class is built to provide a common framework for building "services that rely on the plan" for data connections
 * Example: MilestoneLineService, ActiveColumnService, etc.
 */
export abstract class AbstractCanvasConnection<T> {
	/**
	 * Internal BehaviorSubject to push changes internally without having to expose the actual Subject.
	 * @protected
	 */
	protected _changed$: BehaviorSubject<T | null> = new BehaviorSubject<T>(null);
	/**
	 * Dispatches `T` object when the passed in data changes
	 * Emits null if there is no data OR if a subscription is created before registerPlanData is invoked.
	 */
	public readonly changed$: Observable<T | null> = this._changed$.asObservable();
	/**
	 * The subscription to the current Plan data. Is cleared when registerPlanData is invoked
	 * @protected
	 */
	protected _subscription: Subscription | null = null;
	/**
	 * Cleans up any existing subscription before setting up a new one.
	 */
	protected clearPreviousSubscriber(): void {
		if (!this._subscription) {
			return;
		}

		this._subscription.unsubscribe();
		this._subscription = null;
	}
	/**
	 * Derived classes should override this method and handle incoming data and set up subscriptions.
	 * @param args
	 */
	public registerPlanData(...args: any[]): void {
		this.clearPreviousSubscriber();
	}
	/**
	 * This is manually called by PlanComponent when it is destroyed.
	 * Make sure you add your derived class to the PlanComponent destroy list
	 */
	public destroy(): void {
		this.clearPreviousSubscriber();
	}
}
